import React, { useState } from 'react';
import { SupportRef } from '../../support/SupportRef';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { analysesOfSection, deltaText, refreshedClaim, sectionTitle, turnCount } from '../../stores/slices/reviewsSlice';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import ClaimValue from './ClaimValue';
import ContributeBox from './ContributeBox';
import { targetOfClaim, type ContributionTarget } from './contributions';
import TierChip from './TierChip';
import { traceLinksOn, traceUrl } from '../agent-common/TraceLink';
import type { ConversationThread, ReviewSection } from '../../types';

const KIND_LABEL: Record<string, string> = { draft: 'Draft', watch: 'Watch', 'automate-later': 'Automate later', follow_up: 'Follow-up' };

export function provenance(s: ReviewSection): string {
  const from = s.source?.ref ? `from ${s.source.ref}${s.source.thread_title ? ` (${s.source.thread_title})` : ''}`
    : s.source?.thread_title ? `pinned from ${s.source.thread_title}` : 'pinned from a thread';
  const who = s.pinned_by ? ` by ${s.pinned_by}` : '';
  const when = s.pinned_at ? ` · ${String(s.pinned_at).slice(0, 10)}` : '';
  const refreshed = s.refreshed_at ? ` · refreshed ${String(s.refreshed_at).slice(0, 10)}` : ' · refreshes with this review';
  return `${from}${who}${when}${refreshed}`;
}

/**
 * The walk is its own thread, opened from this section — the review stays a
 * document, not a conversation.
 */
export function askWhySection(section: ReviewSection) {
  const st = useVoyagerStore.getState();
  st.closeVerdictPanel();   // a walk opened from the panel leaves the panel behind
  const claim = refreshedClaim(section);
  useVoyagerStore.setState({ pendingThreadSource: { kind: 'review_section', id: section.id } } as any);
  st.clearLandingChat();
  st.askWhyForClaim(claim as any, sectionTitle(section), { kind: 'review_section', id: section.id });
}

/**
 * What opening a section means, in order: the latest analysis opened FROM it
 * (the digging resumes where it stopped — never a fresh walk that hides it);
 * the thread it was pinned from; a fresh walk when it can be walked (a
 * catalog metric over dated days).
 */
export function opensAs(savedThreads: ConversationThread[], s: ReviewSection): 'analysis' | 'thread' | 'walk' | null {
  if (analysesOfSection(savedThreads, s.id).length) return 'analysis';
  // a thread deleted since the pin is no thread to open: the number's walk instead, when it can be walked
  if (s.source?.thread_id && savedThreads.some((t) => t.id === s.source!.thread_id)) return 'thread';
  const g = s.kind === 'claim' ? refreshedClaim(s).grounding : null;
  return g?.metric_qid && g.window?.start && g.window?.end ? 'walk' : null;
}

/** Open a thread from a review: the side panel, if open, closes — it belongs to the page being left. */
export function goToThread(id: string) {
  const st = useVoyagerStore.getState();
  st.closeVerdictPanel();
  st.loadThread(id);
}

export function openSection(s: ReviewSection) {
  const st = useVoyagerStore.getState();
  const how = opensAs(st.savedThreads, s);
  if (how === 'analysis') goToThread(analysesOfSection(st.savedThreads, s.id)[0].id);
  else if (how === 'thread') goToThread(s.source!.thread_id!);
  else if (how === 'walk') askWhySection(s);
}

/** A significant mover's ⚡ Why: the engine has an explanation ready; the chip opens the walk as its own thread. */
export function WhyChip({ s }: { s: ReviewSection }) {
  if (!s.flag) return null;
  const headline = s.why?.verdict?.headline || 'Significant move — the engine has an explanation ready';
  return (
    <Tooltip title={headline}>
      <Chip
        size="small"
        icon={<BoltIcon sx={{ fontSize: 14 }} />}
        label="Why"
        onClick={() => askWhySection(s)}
        data-testid="review-why-chip"
        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'var(--warn-tint)', color: 'var(--warn)', '& .MuiChip-icon': { color: 'var(--warn)' } }}
      />
    </Tooltip>
  );
}

/** Opens a pinned number's details — where it came from, its analyses, its commentary — in the side panel, never inline. */
export function openSectionDetails(threadId: string, s: ReviewSection, reviewTitle?: string | null) {
  const st = useVoyagerStore.getState();
  const claim = s.kind === 'claim' ? refreshedClaim(s) : null;
  // the number's own perspective: a review built from a deck grounds on every perspective its numbers
  // resolved to (#190), and a correction or a confirmation binds to the number's, not the review's first
  const perspective = claim?.grounding?.perspective || st.savedThreads.find((t) => t.id === threadId)?.doc?.perspective_id || null;
  st.openVerdictPanel({
    kind: 'section', title: sectionTitle(s), why: (s.why || {}) as Record<string, any>, reviewTitle: reviewTitle || null,
    claim, perspective, section: { threadId, sectionId: s.id },
  });
}

const when = (iso: string) => {
  const d = new Date(iso); const today = new Date();
  return d.toDateString() === today.toDateString() ? 'today' : String(iso).slice(0, 10);
};

/**
 * One pinned section: the title (opens its thread, its latest analysis, or its
 * walk), the number, its move, the Why chip, unpin; then the provenance line
 * with its trace, its analyses, driver and events. `row` is the compact form
 * on the page; `panel` adds what a tile keeps for its details — the
 * contribution box and the section's own commentary.
 */
export default function ReviewSectionRow({ threadId, section: s, variant = 'row' }: { threadId: string; section: ReviewSection; variant?: 'row' | 'panel' }) {
  const savedThreads = useVoyagerStore((st) => st.savedThreads);
  const reviewTitle = useVoyagerStore((st) => st.savedThreads.find((t) => t.id === threadId)?.title || null);
  const loadThread = goToThread;
  const unpin = useVoyagerStore((st) => st.unpinReviewSection);
  const setSectionCommentary = useVoyagerStore((st) => st.setReviewSectionCommentary);
  const perspectiveId = useVoyagerStore((st) => st.savedThreads.find((t) => t.id === threadId)?.doc?.perspective_id || null);
  // The contribution binds to the number as it stood when "Add what you know" was pressed: a refresh that
  // lands meanwhile moves the row's window, not the draft's — what was written about August is about August.
  const [contributing, setContributing] = useState<ContributionTarget | null>(null);
  const panel = variant === 'panel';
  const how = opensAs(savedThreads, s);
  const analyses = analysesOfSection(savedThreads, s.id);
  const target = s.kind === 'claim' ? targetOfClaim(refreshedClaim(s), perspectiveId) : null;

  return (
    <Box data-testid="review-pinned" data-kind={s.kind} sx={{ display: 'grid', gap: 0.5, py: panel ? 0 : 1, borderBottom: panel ? 0 : '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {s.kind === 'suggestion' && <Chip size="small" label={KIND_LABEL[s.suggestion?.kind] || s.suggestion?.kind || 'suggestion'} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />}
        {how ? (
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.dark' } }} noWrap
            role="button" tabIndex={0} data-testid="review-pinned-open" data-opens={how}
            title={how === 'analysis' ? 'Resume the latest analysis opened from this number' : how === 'thread' ? 'Open the thread this was pinned from' : 'Open this number\'s walk in its own tab'}
            onClick={() => openSection(s)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSection(s); } }}>
            {sectionTitle(s)}
          </Typography>
        ) : (
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600 }} noWrap data-testid="review-pinned-title">{sectionTitle(s)}</Typography>
        )}
        {panel && target && !contributing && (
          <Chip size="small" label="Add what you know" onClick={() => setContributing(target)} data-testid="review-contribute-chip"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />
        )}
        {s.kind === 'claim' && (
          <ClaimValue claim={refreshedClaim(s)} artifact={{ kind: 'review', thread_id: threadId, section_id: s.id }} sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'primary.dark' }}>
            {formatKpiValue(s.value ?? s.claim?.value, s.claim?.format)}
          </ClaimValue>
        )}
        {s.kind === 'claim' && s.refreshed_at && (
          <Typography variant="caption" sx={{ color: (s.delta || 0) >= 0 ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }} data-testid="review-delta">
            {deltaText(s.delta, s.previous) || (s.previous != null ? 'no change' : '')}
          </Typography>
        )}
        <WhyChip s={s} />
        {!panel && (
          <Tooltip title="Details — where this came from, its analyses, its commentary">
            <IconButton size="small" aria-label={`Details of ${sectionTitle(s)}`} onClick={() => openSectionDetails(threadId, s, reviewTitle)} data-testid="review-pinned-details" sx={{ color: 'text.disabled' }}>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Unpin">
          <IconButton size="small" aria-label={`Unpin ${sectionTitle(s)}`} onClick={() => unpin(threadId, s.id)} sx={{ color: 'text.disabled' }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {panel && contributing && (
        <ContributeBox target={contributing} onSaved={() => undefined} onCancel={() => setContributing(null)} />
      )}
      <Typography
        variant="caption"
        data-testid="review-provenance"
        onClick={() => { if (s.source?.thread_id) loadThread(s.source.thread_id); }}
        sx={{ color: 'text.secondary', cursor: s.source?.thread_id ? 'pointer' : 'default', '&:hover': s.source?.thread_id ? { color: 'primary.dark' } : {} }}
      >
        {provenance(s)}
        <SupportRef session={s.why?.session_id || s.source?.session_id} />
        {traceLinksOn() && (s.why?.session_id || s.source?.session_id) && (
          <a href={traceUrl({ session: s.why?.session_id || s.source?.session_id })} target="_blank" rel="noopener noreferrer" data-testid="review-pin-trace"
            onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6, color: 'inherit' }} title={s.why?.session_id ? 'the walk behind this number' : 'the answer this number was pinned from'}>
            trace
          </a>
        )}
      </Typography>
      {analyses.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="review-section-analyses">
          Analyses:{' '}
          {analyses.slice(0, 3).map((t, i) => (
            <React.Fragment key={t.id}>
              {i > 0 ? ' · ' : ''}
              <Box component="span" role="button" tabIndex={0} onClick={() => loadThread(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') loadThread(t.id); }}
                data-testid="review-section-analysis" sx={{ cursor: 'pointer', color: 'primary.dark', '&:hover': { textDecoration: 'underline' } }}>
                {t.title}
              </Box>
              {` · ${turnCount(t)} turn${turnCount(t) === 1 ? '' : 's'} · ${when(t.updatedAt)}${t.closedAt ? ' · in History' : ''}`}
            </React.Fragment>
          ))}
          {analyses.length > 3 ? ` · +${analyses.length - 3} more` : ''}
        </Typography>
      )}
      {s.error && <Typography variant="caption" sx={{ color: 'var(--bad)' }} data-testid="review-section-error">last refresh failed: {s.error}</Typography>}
      {/* in the panel the verdict box below carries the driver; the row says it here */}
      {((!panel && s.why?.driver?.label) || (s.events || []).length > 0) && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          {!panel && s.why?.driver?.label && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>driver:</Typography>
              <Chip size="small" variant="outlined" label={s.why.driver.label} sx={{ height: 20, fontSize: '0.68rem' }} />
              <TierChip tier={s.why.driver.tier as any} />
            </>
          )}
          {(s.events || []).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} data-testid="review-events">
              {(s.events || []).map((e, i) => <Chip key={`${e.id || i}`} size="small" label={e.title || e.id} sx={{ height: 18, fontSize: '0.62rem' }} />)}
            </Box>
          )}
        </Box>
      )}
      {panel && (
        <TextField
          size="small"
          variant="standard"
          placeholder="Commentary on this number…"
          defaultValue={s.commentary || ''}
          onBlur={(e) => { if (e.target.value !== (s.commentary || '')) setSectionCommentary(threadId, s.id, e.target.value); }}
          inputProps={{ 'aria-label': 'Section commentary', style: { fontSize: 12.5 } }}
        />
      )}
    </Box>
  );
}
